import { FunctionComponent, useCallback, useState } from 'react';
import { MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import { useAppDispatch } from 'src/hooks';
import { USER_STATUS_LABELS, UserStatus } from 'src/enums/user';
import { StatusMenuPaper, StatusSelect } from './styled';
import { updateContributorStatus } from 'src/redux/community/service';
import ConfirmationDialog from 'src/shared/ConfirmationDialog';

type Props = {
    userId: string;
    status: UserStatus;
    lastStatusUpdated?: string;
};

const formatLastStatusUpdated = (raw?: string): string => {
    if (!raw) {
        return 'Last updated: unknown';
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
        return `Last updated: ${raw}`;
    }
    return `Last updated: ${parsed.toLocaleString()}`;
};

const AvailabilityCell: FunctionComponent<Props> = ({
    userId,
    status,
    lastStatusUpdated
}: Props) => {
    const dispatch = useAppDispatch();
    const [pendingArchive, setPendingArchive] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const dispatchUpdate = useCallback(
        (next: UserStatus) => {
            setSubmitting(true);
            dispatch(updateContributorStatus({ userId, status: next })).finally(
                () => setSubmitting(false)
            );
        },
        [dispatch, userId]
    );

    const handleChange = useCallback(
        (event: SelectChangeEvent<UserStatus>) => {
            const next = event.target.value as UserStatus;
            if (next === status) {
                return;
            }
            if (next === UserStatus.ARCHIVE) {
                setPendingArchive(true);
                return;
            }
            dispatchUpdate(next);
        },
        [status, dispatchUpdate]
    );

    const handleConfirmArchive = useCallback(() => {
        setPendingArchive(false);
        dispatchUpdate(UserStatus.ARCHIVE);
    }, [dispatchUpdate]);

    const handleCancelArchive = useCallback(() => {
        setPendingArchive(false);
    }, []);

    return (
        <>
            <Tooltip
                title={formatLastStatusUpdated(lastStatusUpdated)}
                placement="top"
                arrow
            >
                <StatusSelect<UserStatus>
                    value={status}
                    onChange={handleChange}
                    disabled={submitting}
                    size="small"
                    MenuProps={{ PaperProps: { component: StatusMenuPaper } }}
                >
                    {Object.values(UserStatus).map((option) => (
                        <MenuItem key={option} value={option}>
                            {USER_STATUS_LABELS[option]}
                        </MenuItem>
                    ))}
                </StatusSelect>
            </Tooltip>
            <ConfirmationDialog
                open={pendingArchive}
                onClose={handleCancelArchive}
                onConfirm={handleConfirmArchive}
                title="Archive contributor?"
                message="Archiving will remove all of this user's contributor tags and they will be disconnected within an hour. They will not be able to sign back in. Continue?"
                confirmText="Archive"
                cancelText="Cancel"
            />
        </>
    );
};

export default AvailabilityCell;
